import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Axios from "../services/AxiosInterceptor";
import { Mail, ArrowLeft, Send } from 'lucide-react';

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [popupMessage, setPopupMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const showNotice = (msg, isSuccess = false) => {
    setPopupMessage(msg);
    setShowPopup(true);
    setSuccess(isSuccess);
    setTimeout(() => setShowPopup(false), 3500);
  };

  const validateEmail = (value) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      showNotice("Please enter your email.", false);
      return;
    }
    if (!validateEmail(email)) {
      showNotice("Please enter a valid email address.", false);
      return;
    }

    try {
      setLoading(true);
      const res = await Axios.post("api/forgot_password", { email });
      const data = res?.data;

      if (data && data.success) {
        showNotice(data.message || "Reset link sent. Check your email.", true);
        setTimeout(() => navigate("/cx_Login"), 2000);
      } else {
        showNotice(data?.message || "If this email exists, a reset link will be sent.", true);
      }
    } catch (err) {
      console.error("Forgot password error:", err);
      if (err.response?.data?.message) {
        showNotice(err.response.data.message, false);
      } else {
        showNotice("Something went wrong. Please try again.", false);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {showPopup && (
        <div className={`fixed bottom-6 right-6 max-w-sm px-6 py-4 text-white rounded-xl shadow-2xl text-sm font-medium z-[999999999] animate-slide-up ${
          success ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gradient-to-r from-red-500 to-pink-600'
        }`}>
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
            {popupMessage}
          </div>
        </div>
      )}

      <div className="min-h-screen w-full bg-gradient-to-br from-orange-50 via-white to-blue-50 flex items-center justify-center p-4">
        
        <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 bg-white rounded-2xl shadow-2xl overflow-hidden">
          
          {/* Left Side - Branding */}
          <div className="hidden lg:flex relative bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 p-8 flex-col justify-center overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
            
            <div className="relative z-10 text-center">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail size={40} className="text-white" />
              </div>
              <h2 className="text-4xl font-bold text-white mb-4">Password Reset</h2>
              <p className="text-orange-100 text-lg mb-8">We'll help you reset your password quickly</p>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3 text-white bg-white/10 rounded-xl p-3">
                  <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-xs font-bold">1</span>
                  </div>
                  <div className="text-left text-sm">
                    <p className="font-medium">Enter your email</p>
                    <p className="text-xs text-orange-100">We'll send a secure reset link</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 text-white bg-white/10 rounded-xl p-3">
                  <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-xs font-bold">2</span>
                  </div>
                  <div className="text-left text-sm">
                    <p className="font-medium">Check your inbox</p>
                    <p className="text-xs text-orange-100">Click the link in the email</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 text-white bg-white/10 rounded-xl p-3">
                  <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-xs font-bold">3</span>
                  </div>
                  <div className="text-left text-sm">
                    <p className="font-medium">Create new password</p>
                    <p className="text-xs text-orange-100">You're all set!</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="flex flex-col justify-center p-6 sm:p-8 lg:p-10 bg-white">
            
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">Forgot Password?</h1>
              <p className="text-gray-500">No worries! Enter your email and we'll send you a reset link.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Mail size={16} className="text-orange-500" />
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl outline-none transition-all focus:border-orange-400 focus:ring-4 focus:ring-orange-100 text-gray-800"
                  placeholder="Enter your registered email"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Send Reset Link
                  </>
                )}
              </button>
            </form>

            <div className="mt-6">
              <button
                onClick={() => navigate("/cx_Login")}
                className="w-full py-2.5 border-2 border-gray-200 hover:border-orange-300 text-gray-700 font-semibold rounded-xl transition-all flex items-center justify-center gap-2 hover:bg-orange-50"
              >
                <ArrowLeft size={20} />
                Back to Login
              </button>
            </div>

            <div className="lg:hidden mt-6 text-center">
              <p className="text-xs text-gray-400">© 2024 Yogamatic. All rights reserved.</p>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
      `}</style>
    </>
  );
};

export default ForgotPassword;
